import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { USE_OFFLINE_API_MOCK } from '../offline/contract';
import { isMockableUrl, mockFetchResponse } from '../offline/mock-api';
import { tryRefreshAccessToken } from '../offline/auth-refresh';
import { getAccessTokenFromStorage } from '../offline/session-store';
import { shouldPreserveLocalSessionOnAuthError } from '../offline/offline-session-utils';
import { clearSessionStores } from '../offline/clear-all';
import { stopOfflineDataSync } from '../offline/offline-data-sync';

/** Stessa destinazione del logout: login su `/`, non `/login`. Mantiene dati offline locali. */
async function clearSessionAndGoToLogin(navigate) {
  stopOfflineDataSync();
  await clearSessionStores().catch(() => {});
  try {
    localStorage.removeItem('adams_user');
    localStorage.removeItem('token');
  } catch {

    /* ignore */}
  try {
    sessionStorage.clear();
  } catch {

    /* ignore */}
  navigate('/', { replace: true });
}

export default function useHttpApiNew() {
  const navigate = useNavigate();

  const request = useCallback(async (
  url,
  method = 'GET',
  body = null,
  headers = {},
  options = {}) =>
  {
    const { skipAuthRedirect = false, isSyncRequest = false } = options;

    let httpHeaders = { 'Content-type': 'application/json' };
    if (headers) {
      httpHeaders = { ...httpHeaders, ...headers };
    }

    const doFetch = async (hdrs) => {
      if (USE_OFFLINE_API_MOCK && isMockableUrl(url)) {
        const res = await mockFetchResponse(url, {
          method,
          headers: hdrs,
          body: body ? JSON.stringify(body) : null
        });
        return { res, data: await res.json() };
      }
      const res = await fetch(url, {
        method,
        headers: hdrs,
        body: body ? JSON.stringify(body) : null
      });
      const data = await res.json().catch(() => ({}));
      return { res, data };
    };

    try {
      let { res, data } = await doFetch(httpHeaders);

      if (res.status === 401) {
        const online =
        typeof navigator !== 'undefined' && navigator.onLine;
        if (online) {
          const refreshed = await tryRefreshAccessToken();
          if (refreshed) {
            const retryHeaders = {
              ...httpHeaders,
              Authorization: `Bearer ${getAccessTokenFromStorage()}`
            };
            const second = await doFetch(retryHeaders);
            res = second.res;
            data = second.data;
          }
        }
        if (res.status === 401) {
          if (!skipAuthRedirect && !isSyncRequest && !shouldPreserveLocalSessionOnAuthError()) {
            await clearSessionAndGoToLogin(navigate);
            return;
          }
          return { ...data, responseAnyError: true, status: 401 };
        }
      }

      if (data.responseMessage === 'Unathorized') {
        if (!skipAuthRedirect && !isSyncRequest && !shouldPreserveLocalSessionOnAuthError()) {
          await clearSessionAndGoToLogin(navigate);
        }
        return data;
      }

      return data;
    } catch (err) {

      throw err;
    }
  }, [navigate]);

  return { request };
}